import { useState, useEffect, useRef, useCallback } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  LiveKitRoom,
  useParticipants,
  useLocalParticipant,
  useTracks,
  VideoTrack,
  RoomAudioRenderer,
  useChat,
  useRoomContext,
} from '@livekit/components-react';
import '@livekit/components-styles';
import { Track } from 'livekit-client';
import {
  Mic,
  MicOff,
  Video,
  VideoOff,
  PhoneOff,
  MessageSquare,
  Send,
  Users,
  Clock,
  Loader2,
  StopCircle,
  MonitorUp,
  MonitorX,
  LayoutGrid,
  Maximize2,
  BarChart2,
  Languages,
  Settings2,
} from 'lucide-react';
import {
  Avatar,
  AvatarFallback,
  AvatarImage,
  Badge,
  Button,
  Input,
  Label,
  ScrollArea,
  Separator,
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
  useGql,
} from '@ah/design';
import {
  GATHERING_ROOM_QUERY,
  JOIN_GATHERING_MUTATION,
  END_GATHERING_MUTATION,
  SAVE_UTTERANCE_MUTATION,
  GATHERING_STATUS_POLL,
  type GatheringJoinPayload,
  type GatheringUser,
} from '../shared/index.ts';

// ── types ────────────────────────────────────────────────────────────

interface GatheringData {
  id: string;
  title: string;
  endsAt: string;
  status: string;
  host: { id: string };
  participants: { id: string; role: string; user: GatheringUser }[];
}

type ViewMode = 'gallery' | 'focus';

// ── transcription hook ───────────────────────────────────────────────

function useTranscription({ gatheringId, enabled, onUtterance }: {
  gatheringId: string;
  enabled: boolean;
  onUtterance?: (text: string, isFinal: boolean) => void;
}) {
  const gql = useGql();
  const recognitionRef = useRef<any>(null);
  const onUtteranceRef = useRef(onUtterance);
  useEffect(() => { onUtteranceRef.current = onUtterance; }, [onUtterance]);

  useEffect(() => {
    const SpeechRecognition = (window as any).SpeechRecognition || (window as any).webkitSpeechRecognition;
    if (!SpeechRecognition || !enabled) return;

    const recognition = new SpeechRecognition();
    recognition.continuous = true;
    recognition.interimResults = true;
    recognition.lang = 'en-US';
    recognitionRef.current = recognition;

    recognition.onresult = (event: any) => {
      for (let i = event.resultIndex; i < event.results.length; i++) {
        const result = event.results[i];
        const rawText = result[0].transcript.trim();

        if (!result.isFinal) {
          onUtteranceRef.current?.(rawText, false);
        } else {
          onUtteranceRef.current?.(rawText, true);
          if (rawText) {
            const now = new Date().toISOString();
            gql.request(SAVE_UTTERANCE_MUTATION, { gatheringId, text: rawText, startedAt: now, endedAt: now }).catch(() => {});
          }
        }
      }
    };

    recognition.onerror = (e: any) => {
      if (e.error === 'not-allowed') return;
      setTimeout(() => { try { recognition.start(); } catch {} }, 1000);
    };
    recognition.onend = () => {
      if (recognitionRef.current === recognition) try { recognition.start(); } catch {}
    };
    try { recognition.start(); } catch {}

    return () => {
      recognitionRef.current = null;
      try { recognition.stop(); } catch {}
    };
  }, [gatheringId, enabled, gql]);
}

// ── subtitle types ───────────────────────────────────────────────────

interface SubtitleEntry {
  id: string;
  speakerName: string;
  text: string;
  isFinal: boolean;
}

// ── participant tile ─────────────────────────────────────────────────

function GatheringParticipantTile({ trackRef, compact = false, pictureUrl }: { trackRef: any; compact?: boolean; pictureUrl?: string | null }) {
  const hasVideo = trackRef.publication?.isSubscribed !== false && trackRef.publication?.track;
  const mirror = trackRef.participant?.isLocal && trackRef.source === Track.Source.Camera;
  return (
    <div className="relative w-full h-full">
      {hasVideo ? (
        <VideoTrack trackRef={trackRef} className={`w-full h-full object-cover ${mirror ? '-scale-x-100' : ''}`} />
      ) : (
        <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
          <Avatar className={compact ? 'w-8 h-8' : 'w-16 h-16'}>
            <AvatarImage src={pictureUrl ?? ''} />
            <AvatarFallback className={compact ? 'text-xs' : 'text-2xl'}>
              {(trackRef.participant?.name?.[0] ?? '?').toUpperCase()}
            </AvatarFallback>
          </Avatar>
          {!compact && <span className="text-sm text-muted-foreground">{trackRef.participant?.name ?? 'Unknown'}</span>}
        </div>
      )}
      {!compact && (
        <div className="absolute bottom-2 left-2 bg-background/70 backdrop-blur-sm rounded px-2 py-0.5 text-xs text-foreground">
          {trackRef.participant?.name ?? 'Unknown'}
        </div>
      )}
    </div>
  );
}

// ── video area ───────────────────────────────────────────────────────

function VideoArea({ viewMode, participants }: { viewMode: ViewMode; participants: { id: string; role: string; user: GatheringUser }[] }) {
  const cameraTracks = useTracks([{ source: Track.Source.Camera, withPlaceholder: true }]);
  const screenTracks = useTracks([{ source: Track.Source.ScreenShare, withPlaceholder: false }]);
  const room = useRoomContext();
  const userById = Object.fromEntries(participants.map((p) => [p.user.id, p.user]));
  const resolvedPicture = (identity: string): string | null => userById[identity]?.pictureUrl ?? null;
  const [activeSpeakerId, setActiveSpeakerId] = useState<string | null>(null);
  const speakerCandidateRef = useRef<string | null>(null);
  const speakerDebounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    const handler = (speakers: any[]) => {
      const top = speakers[0]?.identity ?? null;
      if (top === speakerCandidateRef.current) return;
      speakerCandidateRef.current = top;
      if (speakerDebounceRef.current) clearTimeout(speakerDebounceRef.current);
      speakerDebounceRef.current = setTimeout(() => setActiveSpeakerId(speakerCandidateRef.current), 2500);
    };
    room.on('activeSpeakersChanged', handler);
    return () => {
      room.off('activeSpeakersChanged', handler);
      if (speakerDebounceRef.current) clearTimeout(speakerDebounceRef.current);
    };
  }, [room]);

  if (viewMode === 'gallery') {
    const count = cameraTracks.length || 1;
    const cols = count === 1 ? 1 : count <= 4 ? 2 : count <= 9 ? 3 : 4;
    const rows = Math.ceil(count / cols);
    return (
      <div className="flex-1 p-4 grid gap-3" style={{ gridTemplateColumns: `repeat(${cols}, minmax(0, 1fr))`, gridTemplateRows: `repeat(${rows}, minmax(0, 1fr))` }}>
        {cameraTracks.map((trackRef, i) => (
          <div key={i} className="relative rounded-xl overflow-hidden bg-muted flex items-center justify-center min-h-0">
            <GatheringParticipantTile trackRef={trackRef} pictureUrl={resolvedPicture(trackRef.participant?.identity ?? '')} />
          </div>
        ))}
      </div>
    );
  }

  // Focus mode
  const hasScreenShare = screenTracks.length > 0;
  const remoteCameraTracks = cameraTracks.filter((t) => !t.participant?.isLocal);
  const focusCandidates = remoteCameraTracks.length > 0 ? remoteCameraTracks : cameraTracks;
  const mainTrack = hasScreenShare
    ? screenTracks[0]
    : (activeSpeakerId ? focusCandidates.find((t) => t.participant?.identity === activeSpeakerId) : null) ?? focusCandidates[0];
  const filmstrip = hasScreenShare ? cameraTracks : cameraTracks.filter((t) => t !== mainTrack);
  const mainLabel = hasScreenShare
    ? `${mainTrack?.participant?.name ?? 'Unknown'}'s screen`
    : (mainTrack?.participant?.name ?? 'Unknown');

  return (
    <div className="flex-1 flex flex-col p-4 gap-3 min-h-0">
      <div className="flex-1 relative rounded-xl overflow-hidden bg-muted min-h-0">
        {mainTrack ? (
          <div className="w-full h-full">
            {mainTrack.publication?.isSubscribed !== false && mainTrack.publication?.track ? (
              <VideoTrack trackRef={mainTrack} className={`w-full h-full ${hasScreenShare ? 'object-contain' : 'object-cover'} ${mainTrack.participant?.isLocal && mainTrack.source === Track.Source.Camera ? '-scale-x-100' : ''}`} />
            ) : (
              <div className="absolute inset-0 flex flex-col items-center justify-center gap-2">
                <Avatar className="w-20 h-20">
                  <AvatarImage src={resolvedPicture(mainTrack.participant?.identity ?? '') ?? ''} />
                  <AvatarFallback className="text-3xl">{(mainTrack.participant?.name?.[0] ?? '?').toUpperCase()}</AvatarFallback>
                </Avatar>
                <span className="text-muted-foreground">{mainTrack.participant?.name ?? 'Unknown'}</span>
              </div>
            )}
          </div>
        ) : (
          <div className="absolute inset-0 flex items-center justify-center"><p className="text-sm text-muted-foreground">No active video</p></div>
        )}
        {mainLabel && <div className="absolute bottom-2 left-2 bg-background/70 backdrop-blur-sm rounded px-2 py-0.5 text-xs text-foreground">{mainLabel}</div>}
      </div>
      {filmstrip.length > 0 && (
        <div className="flex gap-2 shrink-0 overflow-x-auto" style={{ height: '80px' }}>
          {filmstrip.map((trackRef, i) => (
            <div key={i} className="relative w-28 h-full rounded-lg overflow-hidden bg-muted shrink-0">
              <GatheringParticipantTile trackRef={trackRef} compact pictureUrl={resolvedPicture(trackRef.participant?.identity ?? '')} />
              <div className="absolute bottom-1 left-1 bg-background/70 backdrop-blur-sm rounded px-1.5 py-px text-[10px] text-foreground truncate max-w-[96px]">{trackRef.participant?.name ?? 'Unknown'}</div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ── controls bar ─────────────────────────────────────────────────────

function ControlsBar({ isHost, onLeave, onEnd, endLoading, someoneElseSharing }: {
  isHost: boolean;
  onLeave: () => void;
  onEnd: () => void;
  endLoading: boolean;
  someoneElseSharing: boolean;
}) {
  const { localParticipant, isMicrophoneEnabled, isCameraEnabled, isScreenShareEnabled } = useLocalParticipant();
  const [controlsVisible, setControlsVisible] = useState(true);
  const hideTimer = useRef<ReturnType<typeof setTimeout> | null>(null);

  const resetTimer = () => {
    setControlsVisible(true);
    if (hideTimer.current) clearTimeout(hideTimer.current);
    hideTimer.current = setTimeout(() => setControlsVisible(false), 4000);
  };

  useEffect(() => {
    resetTimer();
    window.addEventListener('mousemove', resetTimer);
    return () => {
      window.removeEventListener('mousemove', resetTimer);
      if (hideTimer.current) clearTimeout(hideTimer.current);
    };
  }, []);

  return (
    <div className={`absolute bottom-6 left-0 right-0 flex justify-center pointer-events-none transition-opacity duration-300 ${controlsVisible ? 'opacity-100' : 'opacity-0'}`}>
      <div className="flex items-center gap-3 bg-background/90 backdrop-blur border border-border rounded-2xl px-5 py-3 shadow-lg pointer-events-auto">
        <Button size="icon" variant={isMicrophoneEnabled ? 'secondary' : 'destructive'} className="rounded-full w-11 h-11" onClick={() => localParticipant.setMicrophoneEnabled(!isMicrophoneEnabled)} title={isMicrophoneEnabled ? 'Mute' : 'Unmute'}>
          {isMicrophoneEnabled ? <Mic className="w-5 h-5" /> : <MicOff className="w-5 h-5" />}
        </Button>
        <Button size="icon" variant={isCameraEnabled ? 'secondary' : 'destructive'} className="rounded-full w-11 h-11" onClick={() => localParticipant.setCameraEnabled(!isCameraEnabled)} title={isCameraEnabled ? 'Stop Camera' : 'Start Camera'}>
          {isCameraEnabled ? <Video className="w-5 h-5" /> : <VideoOff className="w-5 h-5" />}
        </Button>
        <Button size="icon" variant={isScreenShareEnabled ? 'default' : 'secondary'} className="rounded-full w-11 h-11" onClick={() => localParticipant.setScreenShareEnabled(!isScreenShareEnabled)} disabled={someoneElseSharing && !isScreenShareEnabled} title={someoneElseSharing && !isScreenShareEnabled ? 'Someone else is sharing' : isScreenShareEnabled ? 'Stop sharing' : 'Share screen'}>
          {isScreenShareEnabled ? <MonitorX className="w-5 h-5" /> : <MonitorUp className="w-5 h-5" />}
        </Button>
        <Separator orientation="vertical" className="h-8" />
        <Button size="icon" variant="destructive" className="rounded-full w-11 h-11" onClick={onLeave} title="Leave gathering">
          <PhoneOff className="w-5 h-5" />
        </Button>
        {isHost && (
          <Button variant="destructive" className="rounded-full px-5" onClick={onEnd} disabled={endLoading} title="End gathering for everyone">
            {endLoading ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : <StopCircle className="w-4 h-4 mr-2" />}
            End for All
          </Button>
        )}
      </div>
    </div>
  );
}

// ── device settings tab ──────────────────────────────────────────────

function DeviceSettingsTab() {
  const room = useRoomContext();
  const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
  const [mics, setMics] = useState<MediaDeviceInfo[]>([]);
  const [activeCam, setActiveCam] = useState('');
  const [activeMic, setActiveMic] = useState('');

  useEffect(() => {
    navigator.mediaDevices.enumerateDevices().then((devices) => {
      setCameras(devices.filter((d) => d.kind === 'videoinput'));
      setMics(devices.filter((d) => d.kind === 'audioinput'));
    });
  }, []);

  const handleCameraChange = async (deviceId: string) => {
    setActiveCam(deviceId);
    if (deviceId) await room.switchActiveDevice('videoinput', deviceId);
  };

  const handleMicChange = async (deviceId: string) => {
    setActiveMic(deviceId);
    if (deviceId) await room.switchActiveDevice('audioinput', deviceId);
  };

  return (
    <ScrollArea className="flex-1 px-3 pb-3">
      <div className="space-y-4 pt-2">
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Camera</Label>
          <select
            className="w-full border rounded px-2 py-1.5 text-sm bg-background text-foreground"
            value={activeCam}
            onChange={(e) => handleCameraChange(e.target.value)}
          >
            <option value="">Default</option>
            {cameras.map((c) => (
              <option key={c.deviceId} value={c.deviceId}>{c.label || 'Camera'}</option>
            ))}
          </select>
        </div>
        <div className="space-y-1.5">
          <Label className="text-xs text-muted-foreground">Microphone</Label>
          <select
            className="w-full border rounded px-2 py-1.5 text-sm bg-background text-foreground"
            value={activeMic}
            onChange={(e) => handleMicChange(e.target.value)}
          >
            <option value="">Default</option>
            {mics.map((m) => (
              <option key={m.deviceId} value={m.deviceId}>{m.label || 'Microphone'}</option>
            ))}
          </select>
        </div>
        <p className="text-xs text-muted-foreground">Changes take effect immediately.</p>
      </div>
    </ScrollArea>
  );
}

// ── right panel ──────────────────────────────────────────────────────

function participantDisplayName(u: GatheringUser): string {
  if (u.firstName) return `${u.firstName} ${u.lastName ?? ''}`.trim();
  return u.name ?? 'Unknown';
}

function RightPanel({ gathering, gatheringId, subtitles }: {
  gathering: GatheringData;
  gatheringId: string;
  subtitles: SubtitleEntry[];
}) {
  const gql = useGql();
  const participants = useParticipants();
  const room = useRoomContext();

  // Look up gathering participant data (name, picture) by LiveKit identity (= user id).
  const gatheringUserById = Object.fromEntries(gathering.participants.map((p) => [p.user.id, p.user]));
  const resolvedName = (identity: string, lkName?: string | null) => {
    const u = gatheringUserById[identity];
    return u ? participantDisplayName(u) : (lkName || identity);
  };
  const resolvedPicture = (identity: string): string | null =>
    gatheringUserById[identity]?.pictureUrl ?? null;
  const { send, chatMessages } = useChat();

  const [activeTab, setActiveTab] = useState('people');
  const [unreadChat, setUnreadChat] = useState(0);
  const [chatInput, setChatInput] = useState('');
  const [sending, setSending] = useState(false);
  const [speakingMs, setSpeakingMs] = useState<Record<string, number>>({});
  const speakingStart = useRef<Record<string, number>>({});
  const chatBottomRef = useRef<HTMLDivElement>(null);
  const subtitleBottomRef = useRef<HTMLDivElement>(null);
  const prevMsgCount = useRef(chatMessages.length);

  // Countdown
  const [msLeft, setMsLeft] = useState(() => Math.max(0, new Date(gathering.endsAt).getTime() - Date.now()));
  useEffect(() => {
    const id = setInterval(() => setMsLeft(Math.max(0, new Date(gathering.endsAt).getTime() - Date.now())), 1000);
    return () => clearInterval(id);
  }, [gathering.endsAt]);
  const totalSecs = Math.floor(msLeft / 1000);
  const h = Math.floor(totalSecs / 3600);
  const m = Math.floor((totalSecs % 3600) / 60);
  const s = totalSecs % 60;
  const timeLeft = `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}:${String(s).padStart(2, '0')}`;

  // Chat unread tracking
  useEffect(() => {
    if (chatMessages.length > prevMsgCount.current && activeTab !== 'chat') {
      setUnreadChat((n) => n + (chatMessages.length - prevMsgCount.current));
    }
    prevMsgCount.current = chatMessages.length;
  }, [chatMessages.length, activeTab]);

  useEffect(() => {
    if (activeTab === 'chat') {
      setUnreadChat(0);
      setTimeout(() => chatBottomRef.current?.scrollIntoView({ behavior: 'smooth' }), 50);
    }
  }, [activeTab, chatMessages.length]);

  useEffect(() => {
    if (activeTab === 'subtitles') subtitleBottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [subtitles.length]); // eslint-disable-line react-hooks/exhaustive-deps

  // Speaking stats
  useEffect(() => {
    const flush = (activeIds: Set<string>) => {
      const now = Date.now();
      setSpeakingMs((prev) => {
        const next = { ...prev };
        Object.keys(speakingStart.current).forEach((id) => {
          next[id] = (next[id] ?? 0) + (now - speakingStart.current[id]);
          if (!activeIds.has(id)) delete speakingStart.current[id];
          else speakingStart.current[id] = now;
        });
        return next;
      });
    };
    const handler = (speakers: any[]) => {
      const now = Date.now();
      const activeIds = new Set(speakers.map((s: any) => s.identity as string));
      speakers.forEach((s: any) => { if (!speakingStart.current[s.identity]) speakingStart.current[s.identity] = now; });
      flush(activeIds);
    };
    const ticker = setInterval(() => {
      const activeIds = new Set(Object.keys(speakingStart.current));
      if (activeIds.size > 0) flush(activeIds);
    }, 1000);
    room.on('activeSpeakersChanged', handler);
    return () => { room.off('activeSpeakersChanged', handler); clearInterval(ticker); };
  }, [room]);

  const handleSendChat = async () => {
    const text = chatInput.trim();
    if (!text || sending) return;
    setSending(true);
    try {
      await send(text);
      // Also persist to DB
      await gql.request(
        `mutation($gatheringId: ID!, $content: String!) { sendGatheringMessage(gatheringId: $gatheringId, content: $content) { id } }`,
        { gatheringId, content: text },
      );
      setChatInput('');
    } catch {} finally {
      setSending(false);
    }
  };

  const totalMs = Object.values(speakingMs).reduce((a, b) => a + b, 0);

  return (
    <div className="w-80 border-l border-border flex flex-col shrink-0 bg-background overflow-hidden">
      <div className="px-4 pt-3 pb-2 shrink-0 border-b border-border">
        <p className="font-semibold text-foreground text-sm truncate">{gathering.title}</p>
        <div className="flex items-center gap-3 mt-1">
          <span className="flex items-center gap-1 text-xs text-muted-foreground"><Clock className="w-3 h-3" /> {timeLeft}</span>
          <Badge variant="default" className="text-xs py-0">
            <span className="mr-1 w-1.5 h-1.5 rounded-full bg-green-400 inline-block animate-pulse" /> Live
          </Badge>
        </div>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="flex-1 flex flex-col min-h-0">
        <TabsList className="mx-3 mt-2 shrink-0 grid grid-cols-5">
          <TabsTrigger value="people" className="text-xs px-1"><Users className="w-3.5 h-3.5" /></TabsTrigger>
          <TabsTrigger value="chat" className="text-xs px-1 relative">
            <MessageSquare className="w-3.5 h-3.5" />
            {unreadChat > 0 && <span className="absolute -top-1 -right-1 w-4 h-4 rounded-full bg-primary text-primary-foreground text-[9px] flex items-center justify-center font-bold">{unreadChat > 9 ? '9+' : unreadChat}</span>}
          </TabsTrigger>
          <TabsTrigger value="stats" className="text-xs px-1"><BarChart2 className="w-3.5 h-3.5" /></TabsTrigger>
          <TabsTrigger value="subtitles" className="text-xs px-1"><Languages className="w-3.5 h-3.5" /></TabsTrigger>
          <TabsTrigger value="settings" className="text-xs px-1"><Settings2 className="w-3.5 h-3.5" /></TabsTrigger>
        </TabsList>

        {/* People tab */}
        <TabsContent value="people" className="flex-1 min-h-0 mt-2 mx-0 data-[state=active]:flex flex-col">
          <ScrollArea className="flex-1">
            <div className="px-3 pb-3 space-y-1">
              {participants.map((p) => (
                <div key={p.sid} className="flex items-center gap-3 rounded-lg px-2 py-2 hover:bg-accent transition-colors">
                  <Avatar className="w-8 h-8 shrink-0">
                    <AvatarImage src={resolvedPicture(p.identity) ?? ''} />
                    <AvatarFallback className="text-xs">{(resolvedName(p.identity, p.name)[0] ?? '?').toUpperCase()}</AvatarFallback>
                  </Avatar>
                  <span className="text-sm text-foreground truncate flex-1">{resolvedName(p.identity, p.name)}</span>
                  <div className="flex items-center gap-1 shrink-0">
                    {!p.isMicrophoneEnabled && <MicOff className="w-3.5 h-3.5 text-muted-foreground" />}
                    {!p.isCameraEnabled && <VideoOff className="w-3.5 h-3.5 text-muted-foreground" />}
                  </div>
                </div>
              ))}
              {participants.length === 0 && <p className="text-xs text-muted-foreground text-center py-6">No other participants yet</p>}
            </div>
          </ScrollArea>
        </TabsContent>

        {/* Chat tab */}
        <TabsContent value="chat" className="flex-1 min-h-0 mt-2 data-[state=active]:flex flex-col">
          <ScrollArea className="flex-1 px-3">
            {chatMessages.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">No messages yet</p>
            ) : (
              <div className="space-y-3 pb-2">
                {chatMessages.map((msg) => (
                  <div key={msg.timestamp} className="flex gap-2">
                    <Avatar className="w-6 h-6 shrink-0">
                      <AvatarImage src={resolvedPicture(msg.from?.identity ?? '') ?? ''} />
                      <AvatarFallback className="text-[10px]">{(resolvedName(msg.from?.identity ?? '', msg.from?.name)[0] ?? '?').toUpperCase()}</AvatarFallback>
                    </Avatar>
                    <div className="max-w-[75%]">
                      <span className="text-[11px] text-muted-foreground">{resolvedName(msg.from?.identity ?? '', msg.from?.name)}</span>
                      <div className="rounded-2xl px-3 py-2 text-sm bg-muted text-foreground rounded-tl-sm">{msg.message}</div>
                    </div>
                  </div>
                ))}
                <div ref={chatBottomRef} />
              </div>
            )}
          </ScrollArea>
          <div className="p-3 border-t border-border shrink-0 flex gap-2">
            <Input placeholder="Type a message..." value={chatInput} onChange={(e) => setChatInput(e.target.value)} onKeyDown={(e) => { if (e.key === 'Enter' && !e.shiftKey) { e.preventDefault(); handleSendChat(); } }} disabled={sending} className="flex-1 h-8 text-sm" />
            <Button size="icon" className="h-8 w-8 shrink-0" onClick={handleSendChat} disabled={!chatInput.trim() || sending}>
              {sending ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Send className="w-3.5 h-3.5" />}
            </Button>
          </div>
        </TabsContent>

        {/* Stats tab */}
        <TabsContent value="stats" className="flex-1 min-h-0 mt-2 data-[state=active]:flex flex-col">
          <ScrollArea className="flex-1 px-3 pb-3">
            {Object.keys(speakingMs).length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">Speaking stats will appear as people talk</p>
            ) : (
              <div className="space-y-3 pt-1">
                {Object.entries(speakingMs).sort((a, b) => b[1] - a[1]).map(([identity, ms]) => {
                  const pct = totalMs > 0 ? Math.round((ms / totalMs) * 100) : 0;
                  const secs = Math.round(ms / 1000);
                  const lkName = participants.find((p) => p.identity === identity)?.name;
                  const label = resolvedName(identity, lkName);
                  return (
                    <div key={identity} className="space-y-1">
                      <div className="flex justify-between text-xs">
                        <span className="font-medium text-foreground truncate max-w-[60%]">{label}</span>
                        <span className="text-muted-foreground shrink-0">{pct}% &middot; {secs}s</span>
                      </div>
                      <div className="h-1.5 rounded-full bg-muted overflow-hidden">
                        <div className="h-full rounded-full bg-primary transition-all duration-500" style={{ width: `${pct}%` }} />
                      </div>
                    </div>
                  );
                })}
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Subtitles tab */}
        <TabsContent value="subtitles" className="flex-1 min-h-0 mt-2 data-[state=active]:flex flex-col">
          <ScrollArea className="flex-1 px-3 pb-3">
            {subtitles.length === 0 ? (
              <p className="text-xs text-muted-foreground text-center py-8">Speech from all participants will appear here</p>
            ) : (
              <div className="space-y-2">
                {subtitles.filter((s) => s.isFinal).slice(-40).map((s) => (
                  <div key={s.id} className="text-xs rounded-lg p-2 bg-muted">
                    <p className="font-medium text-primary mb-0.5">{s.speakerName}</p>
                    <p className="text-foreground">{s.text}</p>
                  </div>
                ))}
                {subtitles.filter((s) => !s.isFinal).map((s) => (
                  <div key={s.id} className="text-xs rounded-lg p-2 bg-muted opacity-60">
                    <p className="font-medium text-primary mb-0.5">{s.speakerName}</p>
                    <p className="text-muted-foreground tracking-widest">...</p>
                  </div>
                ))}
                <div ref={subtitleBottomRef} />
              </div>
            )}
          </ScrollArea>
        </TabsContent>

        {/* Settings tab */}
        <TabsContent value="settings" className="flex-1 min-h-0 mt-2 data-[state=active]:flex flex-col">
          <DeviceSettingsTab />
        </TabsContent>
      </Tabs>
    </div>
  );
}

// ── inner room ───────────────────────────────────────────────────────

function InnerRoom({ gatheringId, gathering, meId, onLeave }: {
  gatheringId: string;
  gathering: GatheringData;
  meId: string;
  onLeave: () => void;
}) {
  const gql = useGql();
  const navigate = useNavigate();
  const room = useRoomContext();
  const isHost = gathering.participants.some((p) => p.role === 'host' && p.user.id === meId);
  const [endLoading, setEndLoading] = useState(false);
  const [viewMode, setViewMode] = useState<ViewMode>('gallery');
  const [subtitles, setSubtitles] = useState<SubtitleEntry[]>([]);
  const subtitleIdRef = useRef(0);

  const screenTracks = useTracks([{ source: Track.Source.ScreenShare, withPlaceholder: false }]);
  const someoneElseSharing = screenTracks.some((t) => !t.participant?.isLocal);

  // Auto-switch view on screen share
  const wasScreenSharingRef = useRef(false);
  useEffect(() => {
    if (screenTracks.length > 0 && !wasScreenSharingRef.current) {
      wasScreenSharingRef.current = true;
      setViewMode('focus');
    } else if (screenTracks.length === 0 && wasScreenSharingRef.current) {
      wasScreenSharingRef.current = false;
      setViewMode('gallery');
    }
  }, [screenTracks.length]);

  const speakerInterimId = useRef<Map<string, string>>(new Map());

  const upsertSubtitle = useCallback((speakerName: string, text: string, isFinal: boolean) => {
    setSubtitles((prev) => {
      const interimId = speakerInterimId.current.get(speakerName);
      if (interimId) {
        if (isFinal) speakerInterimId.current.delete(speakerName);
        return prev.map((s) => s.id === interimId ? { ...s, text, isFinal } : s);
      }
      const id = String(subtitleIdRef.current++);
      if (!isFinal) speakerInterimId.current.set(speakerName, id);
      return [...prev, { id, speakerName, text, isFinal }];
    });
  }, []);

  const handleUtterance = useCallback((text: string, isFinal: boolean) => {
    const speakerName = room.localParticipant.name ?? 'You';
    upsertSubtitle(speakerName, text, isFinal);
    try {
      const payload = new TextEncoder().encode(JSON.stringify({ type: 'transcript', speakerName, text, isFinal }));
      room.localParticipant.publishData(payload, { reliable: isFinal });
    } catch {}
  }, [room, upsertSubtitle]);

  // Receive transcripts from other participants
  useEffect(() => {
    const handler = (payload: Uint8Array) => {
      try {
        const msg = JSON.parse(new TextDecoder().decode(payload));
        if (msg.type === 'transcript' && typeof msg.speakerName === 'string' && typeof msg.text === 'string') {
          upsertSubtitle(msg.speakerName, msg.text, Boolean(msg.isFinal));
        }
      } catch {}
    };
    room.on('dataReceived', handler);
    return () => { room.off('dataReceived', handler); };
  }, [room, upsertSubtitle]);

  const { isMicrophoneEnabled } = useLocalParticipant();
  useTranscription({
    gatheringId,
    enabled: isMicrophoneEnabled,
    onUtterance: handleUtterance,
  });

  // Status transitions (live → ended) are detected by the outer GatheringLive
  // page, which re-renders to the PostGathering recap when it observes the
  // change. We just disconnect cleanly when the component unmounts.

  const handleLeave = async () => {
    await room.disconnect();
    navigate('/gatherings', { replace: true });
  };

  const handleEnd = async () => {
    setEndLoading(true);
    try {
      await gql.request(END_GATHERING_MUTATION, { id: gatheringId });
      // Don't navigate — GatheringLive's status poll will catch the flip to
      // 'ended' and swap in PostGathering. Disconnect the room so the host's
      // LiveKit session shuts cleanly.
      await room.disconnect();
    } catch {
      setEndLoading(false);
    }
  };

  return (
    <div className="flex h-screen bg-background overflow-hidden">
      <div className="flex-1 relative flex flex-col">
        <div className="absolute top-4 right-4 z-40 flex items-center gap-0.5 bg-background/80 backdrop-blur border border-border rounded-lg p-1">
          <Button size="sm" variant={viewMode === 'gallery' ? 'secondary' : 'ghost'} className="h-7 w-7 p-0" onClick={() => setViewMode('gallery')} title="Gallery view"><LayoutGrid className="w-3.5 h-3.5" /></Button>
          <Button size="sm" variant={viewMode === 'focus' ? 'secondary' : 'ghost'} className="h-7 w-7 p-0" onClick={() => setViewMode('focus')} title="Focus view"><Maximize2 className="w-3.5 h-3.5" /></Button>
        </div>
        <VideoArea viewMode={viewMode} participants={gathering.participants} />
        <ControlsBar isHost={isHost} onLeave={handleLeave} onEnd={handleEnd} endLoading={endLoading} someoneElseSharing={someoneElseSharing} />
      </div>
      <RightPanel gathering={gathering} gatheringId={gatheringId} subtitles={subtitles} />
      <RoomAudioRenderer />
    </div>
  );
}

// ── main page ────────────────────────────────────────────────────────

export function GatheringRoom() {
  const { id = '' } = useParams();
  const gql = useGql();
  const navigate = useNavigate();

  const [gathering, setGathering] = useState<GatheringData | null>(null);
  const [meId, setMeId] = useState<string | null>(null);
  const [livekitToken, setLivekitToken] = useState<string | null>(null);
  const [livekitUrl, setLivekitUrl] = useState<string | null>(null);
  const [cameraId, setCameraId] = useState<string | undefined>();
  const [micId, setMicId] = useState<string | undefined>();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const initRan = useRef(false);

  useEffect(() => {
    if (!id || initRan.current) return;
    initRan.current = true;
    const init = async () => {
      try {
        let token = sessionStorage.getItem(`lk_token_${id}`);
        let url = sessionStorage.getItem(`lk_url_${id}`);
        const cam = sessionStorage.getItem(`lk_camera_${id}`) ?? undefined;
        const mic = sessionStorage.getItem(`lk_mic_${id}`) ?? undefined;

        if (!token || !url) {
          // No pre-fetched token — direct link. Call joinGatheringRoom directly
          // so authenticated users can still join via a shared /room URL.
          const joinData = await gql.request<{ joinGatheringRoom: GatheringJoinPayload }>(JOIN_GATHERING_MUTATION, { id });
          token = joinData.joinGatheringRoom.token;
          url = joinData.joinGatheringRoom.livekitUrl;
        }

        sessionStorage.removeItem(`lk_token_${id}`);
        sessionStorage.removeItem(`lk_url_${id}`);
        sessionStorage.removeItem(`lk_camera_${id}`);
        sessionStorage.removeItem(`lk_mic_${id}`);

        setLivekitToken(token);
        setLivekitUrl(url);
        setCameraId(cam);
        setMicId(mic);

        const gatheringData = await gql.request<{ me: { id: string } | null; gathering: GatheringData | null }>(GATHERING_ROOM_QUERY, { id });
        if (!gatheringData.gathering) throw new Error('Gathering not found');
        if (gatheringData.gathering.status === 'ended') {
          // Status flipped between the parent's poll and our join. Let the
          // parent's next tick re-render us as PostGathering — no navigate.
          throw new Error('Gathering has ended');
        }
        setGathering(gatheringData.gathering);
        setMeId(gatheringData.me?.id ?? null);
      } catch (e: unknown) {
        setError(e instanceof Error ? e.message : 'Failed to load gathering');
      } finally {
        setLoading(false);
      }
    };
    init();
  }, [id, navigate, gql]);

  if (loading) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Connecting to gathering...</p>
        </div>
      </div>
    );
  }

  if (error || !gathering || !livekitToken || !livekitUrl) {
    return (
      <div className="h-screen flex items-center justify-center bg-background">
        <div className="text-center space-y-4">
          <p className="text-sm text-destructive">{error ?? 'Failed to connect'}</p>
          <Button onClick={() => navigate('/gatherings')}>Back to Gatherings</Button>
        </div>
      </div>
    );
  }

  return (
    <LiveKitRoom
      token={livekitToken}
      serverUrl={livekitUrl}
      connect={true}
      video={cameraId ? { deviceId: { exact: cameraId } } : true}
      audio={micId ? { deviceId: { exact: micId } } : true}
      // No onDisconnected navigate — the outer GatheringLive page polls the
      // gathering's status and re-renders us out when the host ends the
      // gathering (or when the user clicks Leave inside InnerRoom). Adding a
      // navigate here would race with the parent's state transition and
      // pull the user off /live before the recap can render.
    >
      <InnerRoom gatheringId={gathering.id} gathering={gathering} meId={meId ?? ''} onLeave={() => navigate('/gatherings', { replace: true })} />
    </LiveKitRoom>
  );
}
